import { ShieldCheck, HandshakeIcon, Globe, TrendUp, Buildings } from "@phosphor-icons/react";

/**
 * Homepage-only "Our Global Network" + "Our Global Partners" content section.
 * Inserted between the Case Studies / Testimonials section and the final CTA.
 * Uses existing site design tokens (bone bg, navy accents, sand highlights).
 */
export default function GlobalNetwork() {
  return (
    <>
      {/* ────── Our Global Network ────── */}
      <section
        className="py-24 bg-white border-y border-gos-border"
        data-testid="global-network-section"
      >
        <div className="container-gos">
          <div className="max-w-3xl mb-12">
            <div className="overline mb-3">Reach & credibility</div>
            <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
              Our Global Network
            </h2>
            <p className="text-gos-slate mt-4 leading-relaxed">
              Connecting businesses, markets and opportunities across borders through
              a growing network of trusted international partners.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {NETWORK_ITEMS.map((n) => (
              <div
                key={n.title}
                className="bg-gos-bone border border-gos-border rounded-sm p-6 card-lift"
                data-testid={`network-${n.testid}`}
              >
                <div className="w-11 h-11 flex items-center justify-center bg-gos-navy text-gos-cream rounded-sm mb-4">
                  <n.icon size={20} weight="duotone" />
                </div>
                <h3 className="font-display text-lg text-gos-navy leading-tight">
                  {n.title}
                </h3>
                {n.subtitle && (
                  <p className="text-xs text-gos-muted mt-2 leading-relaxed">
                    {n.subtitle}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ────── Our Global Partners ────── */}
      <section
        className="py-24 bg-gos-bone border-b border-gos-border"
        data-testid="global-partners-section"
      >
        <div className="container-gos">
          <div className="max-w-3xl mb-12">
            <div className="overline mb-3">Trusted collaborators</div>
            <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
              Our Global Partners
            </h2>
            <p className="text-gos-slate mt-4 leading-relaxed">
              Working together with trusted local partners to help businesses expand
              internationally.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PARTNERS.map((p) => (
              <div
                key={`${p.country}-${p.name}`}
                className="bg-white border border-gos-border rounded-sm p-6 flex flex-col h-full card-lift relative"
                data-testid={`partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {/* Country flag + code */}
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl leading-none">{p.flag}</span>
                  <span className="overline !text-gos-muted">{p.country}</span>
                </div>

                {/* Partner name */}
                <h3 className="font-display text-lg text-gos-navy leading-tight">
                  {p.name}
                </h3>

                {/* Description */}
                <p className="text-xs text-gos-slate mt-3 leading-relaxed flex-grow">
                  {p.description}
                </p>

                {/* Badge */}
                <div className="mt-5 pt-4 border-t border-gos-border">
                  {p.comingSoon ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gos-muted border border-gos-border rounded-sm px-2.5 py-1 bg-gos-bone">
                      <span className="w-1.5 h-1.5 rounded-full bg-gos-sand" />
                      Coming Soon
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gos-navy border border-gos-maple/40 rounded-sm px-2.5 py-1 bg-gos-maple/10 font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-gos-maple" />
                      Strategic Partner
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────  DATA  ───────────────────────── */

const NETWORK_ITEMS = [
  {
    title: "Registered with the Government of Canada's Trade Commissioner Service",
    icon: ShieldCheck,
    testid: "tcs",
  },
  {
    title: "Strategic Partners Across Multiple Countries",
    icon: HandshakeIcon,
    testid: "strategic-partners",
  },
  {
    title: "International Business Development",
    icon: Globe,
    testid: "international-bd",
  },
  {
    title: "Trade & Investment Facilitation",
    icon: TrendUp,
    testid: "trade-investment",
  },
  {
    title: "Company Formation & Market Entry Solutions",
    icon: Buildings,
    testid: "company-formation",
  },
];

const PARTNERS = [
  {
    country: "India",
    flag: "🇮🇳",
    name: "Technoglobe",
    description:
      "Business Development, Company Formation Support, Business Advisory & Partner Network",
  },
  {
    country: "Malaysia",
    flag: "🇲🇾",
    name: "MGCTC Corp.",
    description: "Business Expansion, Trade Facilitation & Market Entry",
  },
  {
    country: "Malaysia",
    flag: "🇲🇾",
    name: "AIMS Consultant",
    description:
      "Labuan Companies, Business Setup, Work Permits & Corporate Services",
  },
  {
    country: "Malaysia",
    flag: "🇲🇾",
    name: "NOTA ARIF",
    description:
      "Business Advisory, Corporate Services & Investment Support",
  },
  {
    country: "Indonesia",
    flag: "🇮🇩",
    name: "IDENTIX",
    description: "Business Advisory & Local Market Support",
  },
  {
    country: "UAE",
    flag: "🇦🇪",
    name: "Local Strategic Partner",
    description: "Business Setup & Market Entry",
    comingSoon: true,
  },
  {
    country: "Saudi Arabia",
    flag: "🇸🇦",
    name: "Local Strategic Partner",
    description: "Business Expansion & Business Development",
    comingSoon: true,
  },
  {
    country: "Argentina",
    flag: "🇦🇷",
    name: "Local Strategic Partner",
    description: "Trade & Business Development",
    comingSoon: true,
  },
];
